/**
 * Dashboard principal du système de monitoring
 * Page d'affichage des métriques temps réel avec API réelle (RTK Query)
 * @author GitHub Copilot
 * @date 2025-07-11
 */

import React, { useEffect, useState } from 'react';
import { useMonitoring, useMonitoringFilters, useMonitoringPreferences, useMonitoringAlerts } from '../../../hooks/useMonitoring';
import type { MonitoringDashboardProps, ChartDataPoint } from '../../../types/monitoring.types';
import MetricsCard from '../components/MetricsCard';
import AgentsTable from '../components/AgentsTable';
import PerformanceChart from '../components/PerformanceChart';

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({
  className = '',
  refreshInterval = 30,
  autoRefresh: defaultAutoRefresh = true
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'agents' | 'performance'>('overview');
  
  // Hooks de monitoring avec RTK Query
  const { 
    metrics, 
    summary,
    agents,
    performance,
    formattedMetrics,
    isLoading, 
    isFetching,
    error, 
    lastUpdated, 
    refreshMetrics, 
    clearErrorMessage,
    autoRefresh,
    refreshInterval: currentRefreshInterval,
    toggleAutoRefresh,
    setPollingInterval
  } = useMonitoring({ 
    pollingInterval: defaultAutoRefresh ? refreshInterval * 1000 : 0,
    refetchOnFocus: true 
  });
  
  const { filters, updateFilter } = useMonitoringFilters();
  const { preferences, updatePreference } = useMonitoringPreferences();
  const { alerts, unreadCount, addAlert } = useMonitoringAlerts();

  // Gestion des erreurs avec RTK Query
  useEffect(() => {
    if (error) {
      let errorMessage = 'Erreur lors du chargement des métriques';
      
      if ('data' in error) {
        errorMessage = (error.data as any)?.message || errorMessage;
      } else if ('error' in error) {
        errorMessage = error.error || errorMessage;
      } else if ('message' in error) {
        errorMessage = error.message || errorMessage;
      }
        
      addAlert({
        type: 'error',
        title: 'Erreur de monitoring',
        message: errorMessage,
        actionRequired: true
      });
    }
  }, [error, addAlert]);

  // Données de graphiques
  const responseTimeData: ChartDataPoint[] = useMemo(() => {
    if (!metrics?.performance_history) return [];
    return metrics.performance_history.map(p => ({ timestamp: p.timestamp, value: p.api_avg_latency_ms, label: 'Latence' }));
  }, [metrics]);

  const conversationsData: ChartDataPoint[] = useMemo(() => {
    if (!metrics?.usage_history) return [];
    return metrics.usage_history.map(u => ({ timestamp: u.timestamp, value: u.total_conversations, label: 'Conversations' }));
  }, [metrics]);

  const costData: ChartDataPoint[] = useMemo(() => {
    if (!metrics?.usage_history) return [];
    return metrics.usage_history.map(u => ({ timestamp: u.timestamp, value: u.total_cost_usd, label: 'Coût' }));
  }, [metrics]);

  // Gestion du rafraîchissement manuel
  const handleRefresh = () => {
    refreshMetrics();
    addAlert({
      type: 'success',
      title: 'Actualisation',
      message: 'Dashboard actualisé avec succès',
      actionRequired: false
    });
  };

  // Gestion des erreurs avec retry
  const handleRetry = () => {
    clearErrorMessage();
    refreshMetrics();
  };

  // Rendu de l'état d'erreur si pas de données
  if (error && !metrics) {
    let errorMessage = 'Erreur inconnue';
    if ('data' in error) {
      errorMessage = (error.data as any)?.message || errorMessage;
    } else if ('error' in error) {
      errorMessage = error.error || errorMessage;
    } else if ('message' in error) {
      errorMessage = error.message || errorMessage;
    }

    return (
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center ${className}`}>
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur de connexion</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Réessayer
            </button>
            <button
              onClick={clearErrorMessage}
              className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-200"
            >
              Continuer sans données
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header du dashboard */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Monitoring</h1>
            <p className="text-sm text-gray-600 mt-1">
              {lastUpdated 
                ? `Dernière mise à jour: ${lastUpdated.toLocaleTimeString()}`
                : 'Chargement des données...'
              }
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Alertes */}
            {unreadCount > 0 && (
              <div className="relative">
                <button className="bg-red-100 text-red-600 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors duration-200">
                  🔔 {unreadCount} alerte{unreadCount > 1 ? 's' : ''}
                </button>
              </div>
            )}
            
            {/* Contrôles */}
            <div className="flex items-center space-x-2">
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => toggleAutoRefresh(e.target.checked)}
                  className="mr-2"
                />
                Auto-refresh
              </label>
              
              <select
                value={currentRefreshInterval}
                onChange={(e) => setPollingInterval(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value={5}>5s</option>
                <option value={30}>30s</option>
                <option value={60}>1m</option>
                <option value={300}>5m</option>
              </select>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={isLoading || isFetching}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoading || isFetching ? '⟳' : '🔄'} Actualiser
            </button>
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="mt-4 border-b border-gray-200">
          <div className="flex space-x-8">
            {(['overview', 'agents', 'performance'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'overview' && 'Vue d\'ensemble'}
                {tab === 'agents' && 'Agents'}
                {tab === 'performance' && 'Performance'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Métriques principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricsCard
                title="Conversations totales"
                value={metrics?.summary.total_conversations ?? 0}
                change={5.2}
                changeType="increase"
                icon={<span className="text-blue-600">💬</span>}
                isLoading={isLoading}
              />
              <MetricsCard
                title="Coût total"
                value={metrics?.summary.total_cost_usd ?? 0}
                change={2.1}
                changeType="increase"
                icon={<span className="text-green-600">💰</span>}
                isLoading={isLoading}
              />
              <MetricsCard
                title="Agents actifs"
                value={metrics?.summary.active_agents ?? 0}
                icon={<span className="text-purple-600">👥</span>}
                isLoading={isLoading}
              />
              <MetricsCard
                title="Latence API"
                value={`${metrics?.performance.api_avg_latency_ms ?? 0}ms`}
                change={-3.5}
                changeType="decrease"
                icon={<span className="text-orange-600">⚡</span>}
                isLoading={isLoading}
              />
            </div>

            {/* Graphiques de tendances */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PerformanceChart
                data={responseTimeData}
                title="Temps de réponse (ms)"
                type="line"
                color="#3B82F6"
                isLoading={isLoading}
              />
              <PerformanceChart
                data={conversationsData}
                title="Conversations par heure"
                type="area"
                color="#10B981"
                isLoading={isLoading}
              />
            </div>

            {/* Tableau des agents (version condensée) */}
            {agents && agents.length > 0 && (
              <AgentsTable
                agents={agents.slice(0, 5)}
                isLoading={isLoading}
                onAgentClick={(agentId) => {
                  setActiveTab('agents');
                  // Scroll vers l'agent spécifique
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Gestion des Agents</h2>
              <div className="flex items-center space-x-4 mb-6">
                <select
                  value={filters.dateRange}
                  onChange={(e) => updateFilter({ dateRange: e.target.value as any })}
                  className="border border-gray-300 rounded px-3 py-2"
                >
                  <option value="today">Aujourd'hui</option>
                  <option value="week">Cette semaine</option>
                  <option value="month">Ce mois</option>
                </select>
                
                <label className="flex items-center text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={filters.showInactive}
                    onChange={(e) => updateFilter({ showInactive: e.target.checked })}
                    className="mr-2"
                  />
                  Afficher les agents inactifs
                </label>
              </div>
            </div>

            {agents && agents.length > 0 && (
              <AgentsTable
                agents={agents}
                isLoading={isLoading}
                onAgentClick={(agentId) => {
                  addAlert({
                    type: 'info',
                    title: 'Agent sélectionné',
                    message: `Détails de l'agent ${agentId}`,
                    actionRequired: false
                  });
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PerformanceChart
                data={responseTimeData}
                title="Temps de réponse par agent"
                type="bar"
                color="#F59E0B"
                height={400}
                isLoading={isLoading}
              />
              <PerformanceChart
                data={costData}
                title="Évolution des coûts"
                type="area"
                color="#EF4444"
                height={400}
                isLoading={isLoading}
              />
            </div>

            {/* Métriques de performance */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <MetricsCard
                title="Taux d'erreur"
                value={`${metrics?.performance.error_rate_percent ?? 0}%`}
                change={-1.2}
                changeType="decrease"
                icon={<span className="text-red-600">❌</span>}
                isLoading={isLoading}
              />
              <MetricsCard
                title="Latence moyenne"
                value={`${metrics?.performance.api_avg_latency_ms ?? 0}ms`}
                change={-5.3}
                changeType="decrease"
                icon={<span className="text-blue-600">⚡</span>}
                isLoading={isLoading}
              />
              <MetricsCard
                title="Débit (req/min)"
                value={145}
                change={8.1}
                changeType="increase"
                icon={<span className="text-green-600">📈</span>}
                isLoading={isLoading}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitoringDashboard;
